import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as services from "../services/employeeApi";
import * as service from "../services/salaryApi";
import * as slices from "../slices/employeeSlice";
import { generateRandomString } from "utils/helper";
import { useParams } from "react-router-dom";
import useOptions from "features/masterData/hooks/useOptions";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import alert from "components/elements/Alert";
import { Modal } from "antd";

function useEmployeeEmploymentMovement() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [movementTypeId, setMovementTypeId] = useState("");
  const [month, setMonth] = useState("");
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.employee.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const movementList = useSelector((state) => state.employee.movementList);
  const { movementGroupOptions = [], getMovementGroupOptions } = useOptions();
  const [isMovement, setMovement] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selecttable, settable] = useState(null);



  const modifiedMovementGroupOptions = useMemo(() => {
    return [
      {
        label: i18n.language === "en" ? "All Movement Type" : "การดำเนินการทั้งหมด",
        value: ""
      },
      ...movementGroupOptions
    ];
  }, [i18n.language, movementGroupOptions]);

  const transformMovementData = (data = []) => {
    if (!data || data.length === 0) return [];

    const groupedData = {};

    data.forEach((item) => {
      const { _id, items } = item;
      if (!items || items.length === 0) return;

      if (!groupedData[_id]) {
        const firstItem = items[0];
        groupedData[_id] = {
          _id,
          id: firstItem.id,
          movementTypeName: [],
          movementTypeNameEn: firstItem.movementTypeNameEn,
          oldData: [],
          newData: [],
          approveStatus: firstItem.approveStatus,
          approveStatusEn: firstItem.approveStatusEn,
          effectiveDate: firstItem.effectiveDate,
          createBy: firstItem.createBy,
          createDate: firstItem.createDate,
          approveDate: firstItem.approveDate,
          movementDetails: firstItem.movementDetails,
          movementTypeCode: firstItem.movementTypeCode,
          timeline: firstItem.timeline || [],
        };
      }

      items.forEach(({ movementTypeName, oldData, newData }) => {
        const parseJsonData = (jsonString) => {
          try {
            const parsed = jsonString;
            // console.log(parsed)
            return Object.entries(parsed).reduce((acc, [key, value]) => {
              if (value === null) {
                acc[key] = "-";
              } else if (value["$date"]) {
                const date = new Date(value["$date"]);
                acc[key] = date.toLocaleDateString("en-GB");
              } else if (value.name) {
                if (movementTypeName === "ย้ายสายบังคับบัญชา") {
                  if (i18n.language === "en") {
                    acc[key] = `${value.nameEn} ${value.surNameEn}`;
                  }
                  else {
                    acc[key] = `${value.name} ${value.surName}`;
                  }
                } else {
                  if (i18n.language === "en") {
                    acc[key] = value.nameEn;
                  } else {
                    acc[key] = value.name;
                  }
                }
              } else {
                acc[key] = JSON.stringify(value);
              }
              return acc;
            }, {});
          } catch (error) {
            console.error("Error parsing JSON:", error);
            return {};
          }
        };

        const oldParsed = parseJsonData(oldData);
        const newParsed = parseJsonData(newData);

        groupedData[_id].movementTypeName.push(movementTypeName);
        groupedData[_id].oldData.push(oldParsed);
        groupedData[_id].newData.push(newParsed);
      });
    });

    return Object.values(groupedData).map((group) => ({
      ...group,
      movementTypeName: group.movementTypeName.join(", "),
      oldData: group.oldData
        .map((entry) => Object.entries(entry).map(([key, value]) => `${key}: ${value}`).join("\n"))
        .join("\n\n"),
      newData: group.newData
        .map((entry) => Object.entries(entry).map(([key, value]) => `${key}: ${value}`).join("\n"))
        .join("\n\n"),
    }));
  };



  const getMovement = useCallback(async () => {
    try {
      dispatch(slices.setIsLoading(true));
      const response = await services.getMovement({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: id,
        actionId: currentEmployee,
        movementTypeId,
        month,
      });
      // console.log(response.data);
      const transformedData = transformMovementData(response.data);
      // console.log(transformedData)
      // console.log(response.data);
      dispatch(slices.setMovementList(transformedData));
      // dispatch(slices.setMovementList(response.data));
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [currentCompany, currentEmployee, dispatch, id, month, movementTypeId]);

  const parseDataByMovementType = (oldData, newData, movementTypeName) => {
    if (!movementTypeName) return [];

    const movementTypes = movementTypeName.split(",").map((type) => type.trim());

    let globalIndex = 0;

    const oldParsed = parseKeyValueString(oldData);
    const newParsed = parseKeyValueString(newData);

    return movementTypes.flatMap((movementType) => {
      const allFields = new Set([...Object.keys(oldParsed), ...Object.keys(newParsed)]);

      return Array.from(allFields).map((field) => ({
        movementTypeName: movementType,
        index: ++globalIndex,
        field,
        oldValue: oldParsed[field] || "-",
        newValue: newParsed[field] || "-",
      }));
    });
  };

  const parseKeyValueString = (dataString) => {
    const result = {};
    if (!dataString) return result;

    dataString.split("\n").forEach((line) => {
      const colonIndex = line.indexOf(":");
      if (colonIndex === -1) {
        console.error(`Invalid line format: ${line}`);
        return;
      }

      const key = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();

      if (key) {
        try {
          const parsedValue =
            value.startsWith('[') ? JSON.parse(value) : value;
          if (Array.isArray(parsedValue)) {
            result[key] = parsedValue.map((item) => item.name || "-").join(", ");
          } else if (typeof parsedValue === "object" && parsedValue !== null) {
            result[key] = parsedValue.name || "-";
          } else {
            result[key] = parsedValue || "-";
          }
        } catch (error) {
          console.error(`Error parsing value for key "${key}":`, error);
          result[key] = value || "-";
        }
      }
    });

    return result;
  };

  const updateMovement = useCallback(async (values) => {
    try {
      dispatch(slices.setIsLoading(true));
      setModalOpen(false);
      const request = {
        companyId: currentCompany,
        employeeId: id,
        actionId: currentEmployee,
        movementId: values.id,
        remark: values.remark,
        effectiveDate: values.effectiveDate,
      };
      await service.updateMovement(request);
      await getMovement();
      alert({ message: "Success" });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, currentCompany, id, currentEmployee, getMovement]);

  const isDateString = (value) => {
    const isoDatePattern = /^"?\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3}(Z|[+-]\d{2}:\d{2}))?)?"?$/;
    return typeof value === 'string' && isoDatePattern.test(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    if (!isDateString(dateString)) return dateString;

    const cleanDateString = dateString.replace(/"/g, "");
    const date = new Date(cleanDateString);

    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = (date.getFullYear() + 543).toString();

    return `${day}/${month}/${year}`;
  };


  const formatTableData = (dataArray) => {
    return dataArray.map((data) => ({
      movementTypeName: data.movementTypeName,
      index: data.index,
      field: data.field,
      oldValue: data.oldValue ? formatDate(data.oldValue) : '-',
      newValue: data.newValue ? formatDate(data.newValue) : '-',
    }));
  };


  const deleteMovement = useCallback(async (values) => {
    dispatch(slices.setIsLoading(true));
    try {
      const request = {
        movementId: values,
        companyId: currentCompany,
        employeeId: id,
        actionId: currentEmployee,
      };
      await service.deleteMovement(request);
      await getMovement();
      alert({ message: "Success" });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, currentCompany, id, currentEmployee, getMovement]);

  const onMovementGroupChange = useCallback((value) => setMovementTypeId(value), []);
  const onExpenseDateChange = useCallback((date) => setMonth(date), []);

  const onRowClick = useCallback((record) => {
    if (!record) return;

    const enrichedRecord = {
      ...record,
    };

    setSelectedRow(enrichedRecord);
    setModalOpen(true);


    const tableData = parseDataByMovementType(record?.oldData, record?.newData, record?.movementTypeName);
    settable(formatTableData(tableData));


  }, []);


  const onRowDelete = useCallback(async (values) => {
    try {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: <p>{t("dialog.confirmation.delete").replace("{entity}", t("movement.header"))}</p>,
        onOk() {
          deleteMovement(values);
        },
        onCancel() { },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    } catch (error) {
      console.error(error);
    }
  }, [deleteMovement, t]);

  const onSubmitModal = useCallback(async (values) => {
    dispatch(slices.setIsLoading(true));
    try {
      await updateMovement({ ...values, effectiveDate: dayjs(values.effectiveDate).format("YYYY-MM-DD") });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(slices.setIsLoading(false));
    }
  }, [dispatch, updateMovement]);

  useEffect(() => {
    if (i18n.language)
      getMovement();

  }, [i18n.language, getMovement]);

  useEffect(() => {
    getMovement();
    if (currentCompany && id) {
      getMovementGroupOptions({
        companyId: currentCompany,
        employeeId: id,
      });
    }
  }, [currentCompany, currentEmployee, getMovement, getMovementGroupOptions, id]);

  return {
    movementList,
    movementGroupOptions: modifiedMovementGroupOptions,
    isLoading,
    isMovement,
    isModalOpen,
    setModalOpen,
    setMovement,
    onExpenseDateChange,
    onMovementGroupChange,
    onSubmitModal,
    onRowClick,
    onRowDelete,
    selectedRow,
    selecttable
  };
}

export default useEmployeeEmploymentMovement;
